import { AppServices } from '@singularsystems/neo-core';
import Types from './AppTypes';
import Axios from 'axios';
import { AppConfig } from './AppConfig';
import Slider, { Range } from 'rc-slider';
import 'rc-slider/assets/index.css';
import { SketchPicker } from 'react-color';
import AsyncSelect from 'react-select/async'
import Select from 'react-select';
import { GISTODARApiClient } from '../ApiClients/GISTODARApiClient';
import InstrumentApiClient from '../Maintenance/ApiClients/InstrumentApiClient';
import { AppDataService } from './Services/AppDataService';
import AppLayout from './Services/AppLayout';
import { OidcAuthService } from './Services/AuthenticationService';
import AuthorisationService from '../Authorisation/Services/AuthorisationService';
import { RouteSecurityService } from './Services/RouteSecurityService';
import { RouteService } from './Routes/RouteService';
import ReportingTypes from '@singularsystems/neo-reporting/dist/ReportingTypes';
import AuthorisationTypes from '../Authorisation/AuthorisationTypes';
import HomeApiClient from '../Home/ApiClients/HomeApiClient';
import ServiceProviderApiClient from '../Maintenance/ApiClients/ServiceProviderApiClient';
import GeneralApiClient from '../Common/ApiClients/GeneralApiClient';
import CheckQueryApiClient from '../Maintenance/ApiClients/CheckQueryApiClient';
import CorporateActionEventsApiClient from '../Maintenance/ApiClients/CorporateActionEventsApiClient';
import StaticEventsApiClient from '../Maintenance/ApiClients/StaticEventsApiClient';
import RawGenericMappingApiClient from '../Maintenance/ApiClients/RawGenericMappingApiClient';
import InstrumentTypeApiClient from '../Maintenance/ApiClients/InstrumentTypeApiClient';
import FileGroupingFileSpecApiClient from '../Maintenance/ApiClients/FileGroupingFileSpecApiClient';
import FileGroupingFileSpecImportProcedureApiClient from '../Maintenance/ApiClients/FileGroupingFileSpecImportProcedureApiClient';
import ColumnSpecApiClient from '../Maintenance/ApiClients/ColumnSpecApiClient';
import FileSpecApiClient from '../Maintenance/ApiClients/FileSpecApiClient';
import ClientAccountApiClient from '../DataInterception/ApiClients/ClientAccountApiClient';
import SecurityTransfersApiClient from '../Maintenance/ApiClients/SecurityTransfersApiClient';
import FreeTextEventMappingApiClient from '../DataInterception/ApiClients/FreeTextEventMappingApiClient';
import EventMappingApiClient from '../DataInterception/ApiClients/EventMappingApiClient';
import ExchangeApiClient from '../Maintenance/ApiClients/ExchangeApiClient';
import TransactionsApiClient from '../Reports/ApiClients/TransactionsApiClient';
import InstrumentMappingApiClient from '../DataInterception/ApiClients/InstrumentMappingApiClient';
import FileDownloadApiClient from '../Common/ApiClients/FileDownloadApiClient';
import StaticExportFileSpecApiClient from '../Maintenance/ApiClients/StaticExportFileSpecApiClient';
import FileGroupingExportFileSpecApiClient from "../Maintenance/ApiClients/FileGroupingExportFileSpecApiClient";
import StaticExportColumnSpecApiClient from '../Maintenance/ApiClients/StaticExportColumnSpecApiClient';
import RawGenericExportMappingApiClient from '../Maintenance/ApiClients/RawGenericExportMappingApiClient';
import AuthorisableUpdateRequestApiClient from '../Maintenance/ApiClients/AuthorisableUpdateRequestApiClient';
import ProvisionalTaxReportsApiClient from '../Reports/ApiClients/ProvisionalTaxReportsApiClient';
import CatalogueEditService from '../Maintenance/Catalogue/Services/CatalogueEditService';
import { NeoServicesTypes } from '@singularsystems/neo-react-services';
import StaticDataCache from '../Maintenance/StaticData/StaticDataCache';
import { StaticDataTypes } from '../Maintenance/StaticData/StaticDataTypes';
import TradeOrderApiClient from '../Maintenance/ApiClients/TradeOrderApiClient';
import CurrencyApiClient from '../Maintenance/ApiClients/CurrencyApiClient';
import ExportDataApiClient from '../Maintenance/ApiClients/ExportDataApiClient';

const appModule = new AppServices.Module("App", container => {

    // Config
    container.bind(Types.Config).to(AppConfig).inSingletonScope();
    container.bindConfig(ReportingTypes.ConfigModel, (c: AppConfig) => c.reportingConfig);
    container.bindConfig(AuthorisationTypes.Config.ConfigModel, (c: AppConfig) => c.authorisationConfig);

    // Security
    container.bind(Types.Neo.Security.AuthenticationService).to(OidcAuthService).inSingletonScope();
    container.bind(Types.Neo.Security.AuthorisationService).to(AuthorisationService).inSingletonScope();

    // Api clients
    container.bind(Types.Neo.Axios).toConstantValue(Axios);
    // Global Axios interceptor to help debug failing requests (logs method, URL, status, body).
    Axios.interceptors.response.use(
        (response) => response,
        (error) => {
            try {
                const method = (error?.config?.method || '').toString().toUpperCase();
                const url = error?.config?.url || (error?.config?.baseURL ? `${error.config.baseURL}${error.config.url || ''}` : 'unknown');
                const status = error?.response?.status;
                const respData = error?.response?.data;
                // eslint-disable-next-line no-console
                console.error(`[Axios Error] ${method} ${url} -> ${status}`, respData || error?.message || error);
            } catch (e) {
                // ignore logging errors
            }

            return Promise.reject(error);
        }
    );
    container.bind(Types.ApiClients.GISTODARApiClient).to(GISTODARApiClient).inSingletonScope();
    container.bind(Types.ApiClients.InstrumentApiClient).to(InstrumentApiClient).inSingletonScope();
    container.bind(Types.ApiClients.HomeApiClient).to(HomeApiClient).inSingletonScope();
    container.bind(Types.ApiClients.ServiceProviderApiClient).to(ServiceProviderApiClient).inSingletonScope();
    container.bind(Types.ApiClients.GeneralApiClient).to(GeneralApiClient).inSingletonScope();
    container.bind(Types.ApiClients.CheckQueryApiClient).to(CheckQueryApiClient).inSingletonScope();
    container.bind(Types.ApiClients.CorporateActionEventsApiClient).to(CorporateActionEventsApiClient).inSingletonScope();
    container.bind(Types.ApiClients.TradeOrderApiClient).to(TradeOrderApiClient).inSingletonScope();
    container.bind(Types.ApiClients.CurrencyApiClient).to(CurrencyApiClient).inSingletonScope();
    container.bind(Types.ApiClients.StaticEventsApiClient).to(StaticEventsApiClient).inSingletonScope();
    container.bind(Types.ApiClients.RawGenericMappingApiClient).to(RawGenericMappingApiClient).inSingletonScope();
    container.bind(Types.ApiClients.InstrumentTypeApiClient).to(InstrumentTypeApiClient).inSingletonScope();
    container.bind(Types.ApiClients.FileGroupingFileSpecApiClient).to(FileGroupingFileSpecApiClient).inSingletonScope();
    container.bind(Types.ApiClients.FileGroupingFileSpecImportProceduresApiClient).to(FileGroupingFileSpecImportProcedureApiClient).inSingletonScope();
    container.bind(Types.ApiClients.ColumnSpecApiClient).to(ColumnSpecApiClient).inSingletonScope();
    container.bind(Types.ApiClients.FileSpecApiClient).to(FileSpecApiClient).inSingletonScope();
    container.bind(Types.ApiClients.ClientAccountApiClient).to(ClientAccountApiClient).inSingletonScope();
    container.bind(Types.ApiClients.SecurityTransfersApiClient).to(SecurityTransfersApiClient).inSingletonScope();
    container.bind(Types.ApiClients.FreeTextEventMappingApiClient).to(FreeTextEventMappingApiClient).inSingletonScope();
    container.bind(Types.ApiClients.EventMappingApiClient).to(EventMappingApiClient).inSingletonScope();
    container.bind(Types.ApiClients.ExchangeApiClient).to(ExchangeApiClient).inSingletonScope();
    container.bind(Types.ApiClients.TransactionsApiClient).to(TransactionsApiClient).inSingletonScope();
    container.bind(Types.ApiClients.InstrumentMappingApiClient).to(InstrumentMappingApiClient).inSingletonScope();
    container.bind(Types.ApiClients.FileDownloadApiClient).to(FileDownloadApiClient).inSingletonScope();
    container.bind(Types.ApiClients.StaticExportFileSpecApiClient).to(StaticExportFileSpecApiClient).inSingletonScope();
    container.bind(Types.ApiClients.FileGroupingExportFileSpecApiClient).to(FileGroupingExportFileSpecApiClient).inSingletonScope();
    container.bind(Types.ApiClients.StaticExportColumnSpecApiClient).to(StaticExportColumnSpecApiClient).inSingletonScope();
    container.bind(Types.ApiClients.RawGenericExportMappingApiClient).to(RawGenericExportMappingApiClient).inSingletonScope();
    container.bind(Types.ApiClients.AuthorisableUpdateRequestApiClient).to(AuthorisableUpdateRequestApiClient).inSingletonScope();
    container.bind(Types.ApiClients.ProvisionalTaxReportsApiClient).to(ProvisionalTaxReportsApiClient).inSingletonScope();
    container.bind(Types.ApiClients.ExportDataApiClient).to(ExportDataApiClient).inSingletonScope();


    // Services
    container.bind(Types.Services.AppDataCache).to(AppDataService).inSingletonScope();
    container.bind(Types.Services.AppLayout).to(AppLayout).inSingletonScope();
    container.bind(Types.Services.RouteService).to(RouteService).inSingletonScope();
    container.bind(Types.Neo.Routing.RouteSecurityService).to(RouteSecurityService).inSingletonScope();
    container.bind(NeoServicesTypes.Catalogue.CatalogueEditService).to(CatalogueEditService).inSingletonScope();
    container.bind(StaticDataTypes.Services.StaticDataCache).to(StaticDataCache).inSingletonScope();

    // Components
    container.bind(Types.Neo.Components.Slider).toConstantValue(Slider);
    container.bind(Types.Neo.Components.Range).toConstantValue(Range);
    container.bind(Types.Neo.Components.SketchPicker).toConstantValue(SketchPicker);
    container.bind(Types.Neo.Components.AsyncSelect).toConstantValue(AsyncSelect);
    container.bind(Types.Neo.Components.ReactSelect).toConstantValue(Select);
});

const appTestModule = new AppServices.Module("App", container => {

});

export { appModule, appTestModule };